import { OPTIONAL_AUTH_PATH_REGEX } from "~vue/api";

import { CSRF_REFRESH_URL, refreshCsrfTokenResult } from "./csrf";
import { logError } from "./logError";
import { CSRF_STALE_TOKEN, declineRecovery, reportSessionEnded, SESSION_EXPIRED, sessionEndedCodeFrom } from "./sessionEnded";

/**
 * Paths that must never be recovered, because recovery would call back into them.
 *
 * The refresh endpoint is the obvious one. The error reporter is the subtler one: a failed
 * recovery logs, logging POSTs here, and a 403 on that POST would start another recovery.
 */
const NEVER_RECOVER = new Set<string>([CSRF_REFRESH_URL, "/events/error/"]);

/** Our own origin, with a stand-in for the non-browser contexts the module is imported in. */
function ownOrigin(): string {
    return globalThis.location?.origin ?? "http://localhost";
}

/** The raw URL out of whichever of fetch's three input forms the caller used. */
function urlOf(input: RequestInfo | URL): string {
    return typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
}

/**
 * Whether a request is going to our own origin. Relative URLs are the common case, hence the base.
 *
 * Everything this module does is scoped by this: our CSRF token, our session, our `code`
 * contract. An unparsable URL counts as foreign, because nothing here should act on a request
 * it cannot even locate.
 */
function isSameOrigin(input: RequestInfo | URL): boolean {
    try {
        return new URL(urlOf(input), ownOrigin()).origin === ownOrigin();
    } catch {
        return false;
    }
}

/**
 * Resolve the path from whichever of fetch's three input forms the caller used.
 * Only meaningful for same-origin requests -- check :func:`isSameOrigin` first.
 */
function pathOf(input: RequestInfo | URL): string {
    try {
        return new URL(urlOf(input), ownOrigin()).pathname;
    } catch {
        return "";
    }
}

/**
 * Read a rejected response's body without consuming it, since the caller still has to read it.
 */
async function peekBody(response: Response): Promise<{ login_url?: string } | null> {
    try {
        return (await response.clone().json()) as { login_url?: string };
    } catch {
        // A non-JSON body is not one of ours -- a WAF block page, say. Nothing to report, and
        // nothing worth logging: this runs on every 403 in the app, including expected ones.
        return null;
    }
}

function withCsrfToken(init: RequestInit | undefined, token: string): RequestInit {
    const headers = new Headers(init?.headers);
    headers.set("X-CSRFToken", token);
    return { ...init, headers };
}

/**
 * Mark same-origin requests as XHR callers, so a plain Django view's CSRF 403 arrives as a JSON
 * body carrying a `code` this wrapper can act on, instead of the rendered ChatError page.
 *
 * `_wants_machine_response` keys on `Sec-Fetch-Mode` first, which browsers send on every fetch;
 * this header is the fallback for embedded webviews that send none. Same-origin only, because a
 * custom header on a cross-origin request (Sentry, third parties) would force a CORS preflight
 * the other end may reject. An explicit caller-set value is left alone.
 */
function withMachineResponseMarker(input: RequestInfo | URL, init: RequestInit | undefined): RequestInit | undefined {
    if (!isSameOrigin(input)) {
        return init;
    }
    // Seeded from the Request's own headers when init carries none, since passing init.headers to
    // fetch replaces the Request's headers wholesale rather than merging.
    const headers = new Headers(init?.headers ?? (input instanceof Request ? input.headers : undefined));
    if (headers.has("X-Requested-With")) {
        return init;
    }
    headers.set("X-Requested-With", "XMLHttpRequest");
    return { ...init, headers };
}

/**
 * Answer a stale-token 403 by fetching a fresh token and reissuing the request once.
 *
 * Retrying is safe even for non-idempotent writes: `CsrfViewMiddleware` rejects in
 * `process_view` and DRF rejects during authentication, both before the view body runs, so the
 * first attempt had no side effect.
 */
async function recoverStaleToken(
    input: RequestInfo | URL,
    init: RequestInit | undefined,
    loginUrl: string | null,
    nativeFetch: typeof globalThis.fetch,
    rejected: Response,
): Promise<Response> {
    const refresh = await refreshCsrfTokenResult();

    // Compared against null rather than tested for falsiness, so the union narrows to the
    // failure arm and `failure` is reachable.
    if (refresh.token === null) {
        if (refresh.failure === "unauthenticated") {
            // The session died between the two requests, so there is nothing to retry into.
            reportSessionEnded(SESSION_EXPIRED, loginUrl);
        }
        // "unavailable" is a 5xx, a dropped connection or a malformed body. The session may be
        // perfectly fine, so the caller gets its original failure and the ordinary error handling
        // rather than a dialog telling it to log back in.
        //
        // Marked so the onFetchError backstop in ~vue/api defers to that choice instead of
        // reporting the same coded 403 itself. Ownership is one-way: the wrapper decides.
        declineRecovery(rejected);
        return rejected;
    }

    const retried = await nativeFetch(input, withCsrfToken(init, refresh.token));
    if (retried.status !== 403) {
        return retried;
    }

    // A fresh token can still meet a genuine permission denial or a WAF block, so classify the
    // second rejection rather than assuming it is another stale token.
    const retriedBody = await peekBody(retried);
    const retriedCode = sessionEndedCodeFrom(retriedBody);

    if (retriedCode === SESSION_EXPIRED) {
        reportSessionEnded(SESSION_EXPIRED, retriedBody?.login_url ?? loginUrl);
    } else if (retriedCode === CSRF_STALE_TOKEN) {
        // A token the server issued seconds ago was rejected, which should not happen. Surface
        // the dialog so the user is not stuck, and log it because something is wrong beyond a
        // stale page.
        logError(new Error("CSRF retry rejected a freshly issued token"), {
            context: { fetch: { status: retried.status } },
            tags: { error_type: "csrf_retry_failed" },
        });
        reportSessionEnded(CSRF_STALE_TOKEN, retriedBody?.login_url ?? loginUrl);
    }

    // Anything unrecognized -- permission, WAF -- is left for the caller's existing handling.
    return retried;
}

/**
 * Decide what a 403 means and, where possible, undo it.
 */
async function handleForbidden(response: Response, input: RequestInfo | URL, init: RequestInit | undefined, nativeFetch: typeof globalThis.fetch): Promise<Response> {
    // Before anything reads the body. A third party's 403 is not ours to interpret: CORS makes
    // plenty of them readable, `code` is not a reserved word, and the path-only checks below
    // would happily match a foreign URL. Acting on one would refresh our token off the back of
    // someone else's response and replay their request carrying our X-CSRFToken header.
    if (!isSameOrigin(input)) {
        return response;
    }

    const path = pathOf(input);

    // An external 360 rater has no account, so offering them a login would be nonsense. Their
    // 403 means the invite is bad or spent.
    if (OPTIONAL_AUTH_PATH_REGEX.test(path) || NEVER_RECOVER.has(path)) {
        return response;
    }

    const body = await peekBody(response);
    const code = sessionEndedCodeFrom(body);
    if (!code) {
        return response;
    }

    if (code === SESSION_EXPIRED) {
        // Nothing to retry into, and each attempt would only delay the dialog.
        reportSessionEnded(SESSION_EXPIRED, body?.login_url ?? null);
        return response;
    }

    return recoverStaleToken(input, init, body?.login_url ?? null, nativeFetch, response);
}

/**
 * Detect a dead session, and repair a stale token, for every `fetch` in the app.
 *
 * Wrapping the global is deliberate and is the point of this module. The alternative is a hook
 * per transport, which is what we had: two shared layers covered themselves, while the files
 * calling `fetch` directly each needed wiring by hand, and each new one arrived as a QA finding.
 * Everything funnels through `fetch` except Inertia, which uses axios over XHR and is handled by
 * the `invalid` listener in vue.js.
 *
 * Recovery lives here rather than in a per-transport hook so that detecting the failure and
 * undoing it are one sequence. Held as two layers they raced: the wrapper would open a
 * "reload the page" dialog while a retry elsewhere was still in flight, and because the dialog
 * is first-report-wins the eventual success could not take it back -- leaving someone told to
 * reload after their write had already landed, and repeating it if they did.
 *
 * The rule stays narrow: it acts on our own `code` in the body, never on the status alone, so a
 * WAF block, an ordinary permission denial, and a Microsoft Teams login check pass through
 * untouched.
 *
 * Install before the app mounts. vueuse resolves `window.fetch` when a composable is created,
 * which is after mount, so both it and any later direct caller pick up the wrapper.
 */
export function installSessionEndedFetch(): void {
    const nativeFetch = globalThis.fetch.bind(globalThis);

    globalThis.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
        const markedInit = withMachineResponseMarker(input, init);
        const response = await nativeFetch(input, markedInit);

        // Status first, so a streaming success is never cloned.
        if (response.status !== 403) {
            return response;
        }

        return handleForbidden(response, input, markedInit, nativeFetch);
    };
}
